import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ConfirmationDialog from '../components/common/ConfirmationDialog';

interface ConfirmDialogOptions {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  isDanger?: boolean;
}

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isDanger?: boolean;
  isLoading?: boolean;
}

interface ConfirmDialogContextValue {
  confirm: (options: ConfirmDialogOptions) => Promise<boolean>;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextValue | undefined>(undefined);

export const ConfirmDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ConfirmDialogState>({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Potvrdit',
    cancelText: 'Zrušit',
    isDanger: false,
    isLoading: false,
  });

  const pendingRef = useRef<{ resolve: (value: boolean) => void } | null>(null);

  const closeDialog = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false, isLoading: false }));
    if (pendingRef.current) {
      pendingRef.current.resolve(false);
      pendingRef.current = null;
    }
  }, []);

  const handleConfirm = useCallback(() => {
    setState((prev) => ({ ...prev, isLoading: false, isOpen: false }));
    if (pendingRef.current) {
      pendingRef.current.resolve(true);
      pendingRef.current = null;
    }
  }, []);

  const confirm = useCallback((options: ConfirmDialogOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      pendingRef.current = { resolve };
      setState({
        isOpen: true,
        title: options.title || 'Potvrzení',
        message: options.message || '',
        confirmText: options.confirmText || 'Potvrdit',
        cancelText: options.cancelText || 'Zrušit',
        isDanger: options.isDanger || false,
        isLoading: false,
      });
    });
  }, []);

  return (
    <ConfirmDialogContext.Provider value={{ confirm }}>
      {children}
      <ConfirmationDialog
        isOpen={state.isOpen}
        onClose={closeDialog}
        onConfirm={handleConfirm}
        title={state.title}
        message={state.message}
        confirmText={state.confirmText}
        cancelText={state.cancelText}
        isDanger={state.isDanger}
        isLoading={state.isLoading}
      />
    </ConfirmDialogContext.Provider>
  );
};

export const useConfirmDialog = (): ConfirmDialogContextValue => {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error('useConfirmDialog must be used within ConfirmDialogProvider');
  }
  return ctx;
};
